import * as React from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import ExpandMore from '@mui/icons-material/ExpandMore'

import { OverflowDetector } from '@sales-center/components'
import { LocationLink } from '@user/components/Location'
import { BillTo } from '@user/store/entityLocations'
import { useToggleBoolean } from '@user/utils/useToggleBoolean'

import { useData } from './hooks/useData'
import { highlightText } from '../../highlightText'

type Props = {
  location: BillTo
  splitAddressIndex?: number
  searchText?: string
  onSelectBillTo?: () => void
  viewOnly?: boolean
}

export function BillToSearchSelectContents(props: Props) {
  const { location, searchText, onSelectBillTo, viewOnly } = props

  const [open, toggleOpen] = useToggleBoolean()
  const { properties } = useData(props)
  const hasMoreFields = properties.length > 2
  const verb = 'Details'

  const renderProperty = (property: string) => {
    return searchText ? highlightText(property, searchText) : property
  }

  return (
    <Box
      position={'relative'}
      sx={{ cursor: viewOnly ? 'default' : 'pointer' }}
    >
      <Box onClick={!viewOnly ? onSelectBillTo : undefined}>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            lineHeight: '20px',
            mb: '10px',
          }}
        >
          Bill - to
        </Typography>
        <LocationLink
          variant="subtitle2"
          externalLocationCode={location.number}
          externalLocationId={location.id}
          billingLocation
        />
        <OverflowDetector open={open} closedHeight={42}>
          {properties.map((property) => (
            <Typography key={property} variant="body2">
              {renderProperty(property)}
            </Typography>
          ))}
        </OverflowDetector>
      </Box>
      {hasMoreFields && (
        <Button
          variant="text"
          size="small"
          sx={{
            textTransform: 'capitalize',
          }}
          onClick={toggleOpen}
          data-test-id="billToDetailsButton"
        >
          {verb}
          <ExpandMore
            sx={{
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transitionProperty: 'all',
              transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
              transitionDuration: '150ms',
            }}
          />
        </Button>
      )}
    </Box>
  )
}
